import React, { useEffect, useRef, useState } from 'react'
import { EditableTextStyled, ErrorMessage, ClickableLabel } from './styled'

export interface TextInputProps {
  onNameChange: (newValue: string) => void
  value: string
  errorMessage?: string
  isEditable?: boolean
  alwaysEditable?: boolean
  onBlurSave?: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  label?: string
  isRequired?: boolean
  disabled?: boolean
}

export const EditableText: React.FC<TextInputProps> = ({
  value,
  errorMessage,
  onNameChange,
  alwaysEditable = false,
  onBlurSave,
  onBlur,
  placeholder,
  label,
  isRequired,
  disabled,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(alwaysEditable)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const initialValueRef = useRef<string>(value)

  const handleLabelClick = () => {
    if (props.isEditable) {
      setIsEditing(true)
    }
  }

  const handleSave = () => {
    onBlur?.()
    
    if (!alwaysEditable) {
      if (!value.trim()) {
        onNameChange(initialValueRef.current)
        setIsEditing(false)
        return
      }
      setIsEditing(false)
    }

    if (onBlurSave && value.trim() && value !== initialValueRef.current) {
      onBlurSave(value)
      initialValueRef.current = value
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      inputRef.current?.blur()
    }
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const inputWidth = alwaysEditable ? '280px' : `${(value.length + 3) * 8}px`
  const hasError = Boolean(errorMessage)

  if (!alwaysEditable) {
    return (
      <EditableTextStyled $alwaysEditable={false} $hasError={hasError}>
        <div className='input-wrapper'>
          {isEditing ? (
            <input
              ref={inputRef}
              style={{ width: inputWidth }}
              value={value}
              title={value}
              placeholder={placeholder}
              onChange={(e) => onNameChange(e.target.value)}
              onBlur={handleSave}
              onKeyDown={handleKeyDown}
              maxLength={100}
            />
          ) : (
            <ClickableLabel
              onClick={handleLabelClick}
              $isEditable={props.isEditable}
            >
              {value}
            </ClickableLabel>
          )}
        </div>
      </EditableTextStyled>
    )
  }

  return (
    <EditableTextStyled $alwaysEditable={alwaysEditable} $hasError={hasError}>
      {label && (
        <label className='input-label'>
          {label}
          {isRequired && !disabled && <span className='required-star'>*</span>}
        </label>
      )}
      <div className='input-wrapper'>
        <input
          ref={inputRef}
          style={{ width: inputWidth }}
          value={value}
          title={value}
          placeholder={placeholder}
          onChange={(e) => onNameChange(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          maxLength={100}
          disabled={disabled}
        />
      </div>
      {hasError && <ErrorMessage>Required</ErrorMessage>}
    </EditableTextStyled>
  )
}
